import useTabStore from "@/stores/useTabStore";
import { useOnboardingStore } from "../useOnboardingStore";
import type { SpotlightControl } from "@/contexts/WidgetSpotlightContext";
import { RAIL_STEPS } from "./steps";
import { resolveRailTarget } from "./railTargets";

/**
 * Which widget the rail is currently pointing at, on the tab they are looking
 * at.
 *
 * Resolved in the gate rather than inside the rail so the spotlight provider
 * wraps the dashboard whether or not the rail itself renders — same reasoning
 * as DemoGuideGate, which does this for the same reason.
 *
 * Null is a normal outcome: the user may be standing on a tab with no headlines
 * panel on it. The rail keeps reading; only the ring goes away.
 */
export function useRailTarget(): {
  targetWidgetId: string | null;
  targetControl: SpotlightControl;
} {
  const rail = useOnboardingStore((s) => s.state?.rail);
  const activeTab = useTabStore((s) =>
    s.tabs.find((tab) => tab.id === s.activeTabId)
  );

  if (!rail || rail.completedAt || rail.exitedAt) {
    return { targetWidgetId: null, targetControl: null };
  }

  const stepId = rail.stepIds[rail.step];
  const step = RAIL_STEPS.find((s) => s.id === stepId);
  if (!step?.target) return { targetWidgetId: null, targetControl: null };

  return {
    targetWidgetId: resolveRailTarget(activeTab, step.target),
    targetControl: step.control ?? null,
  };
}
